import React from "react";
import { Route, Redirect, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUser, useSigninCheck } from "reactfire";
import { useGame } from "context/gameContext";
import LoadingComponent from "components/loading";
import MaintananceComponent from "components/maintanance";
import {
  IonRouterOutlet,
  IonTabs,
  IonTabBar,
  IonTabButton,
  IonLabel,
} from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { SportsProvider } from "context/sportsContext";
import {
  Md2Home,
  Md2SoccerField,
  Md2SearchWeb,
  Md2EmoticonExcitedOutline,
  Md2TrophyVariant,
  Md2TrophyVariantOutline,
} from "react-icons-ng/md2";
import LobbyRoutes from "./leagues/lobbyRoute";
import Explore from "./explore";
import Notifications from "./header/notifications";
import DeepLinks from "./header/deepLinks";
import NewUserOnboarding from "./login/newUserOnboarding";

const FootballData = React.lazy(() => import("views/footballData"));
const Settings = React.lazy(() => import("views/settings"));
const Login = React.lazy(() => import("views/login/"));
const Support = React.lazy(() => import("views/support/"));
const TermsView = React.lazy(() => import("views/terms/"));
const Highscores = React.lazy(() => import("views/highscores"));

const AppRoutes = () => {
  const { status: userStatus, data: user } = useUser();
  const { status: signInStatus, data: signInCheckResult } = useSigninCheck();

  const {
    state: { settings, hideBottomBar },
    dispatch,
  } = useGame();

  const { t } = useTranslation();

  let maintananceMode = settings?.maintananceMode;

  if (maintananceMode) return <MaintananceComponent />;
  if (userStatus !== "success" || signInStatus !== "success")
    return <LoadingComponent green={false} text={t("common.loading")} />;

  const routes = (
    <>
      <Route
        path="/tabs/:tab(tournaments)"
        render={(props) => {
          return user == null ? <Login /> : <LobbyRoutes {...props} />;
        }}
      />

      <Route
        exact
        path="/support"
        render={(props) => {
          return <Support />;
        }}
      />
      <Route
        exact
        path="/terms"
        render={(props) => {
          return <TermsView />;
        }}
      />

      <Route
        exact
        path="/completeSignup"
        render={(props) => {
          const params = new URLSearchParams(props.location.search);
          const mode = params.get("mode");
          const oobCode = params.get("oobCode");
          const url = props?.location?.state?.url
            ? props.location.state.url
            : window.location.href;

          const isNewSocialUser = props?.location?.state?.isNewSocialUser;

          if (!mode) return <Redirect to="/" />;

          if (mode === "signIn") {
            return <NewUserOnboarding url={url} social={false} />;
          } else if (mode === "onBoard") {
            return (
              <NewUserOnboarding
                social={true}
                isNewSocialUser={isNewSocialUser}
              />
            );
          } else {
            return <Explore {...props} />;
          }
        }}
      />

      <Route exact path="/">
        <Redirect to="/tabs/tournaments" />
      </Route>
      <Route
        exact
        path="/tabs/:tab(explore)"
        render={(props) => {
          return user == null ? <Login /> : <Explore {...props} />;
        }}
      />
      <Route
        exact
        path="/tabs/:tab(footballData)"
        render={(props) => {
          return user == null ? (
            <Login />
          ) : (
            <SportsProvider>
              <FootballData {...props} />
            </SportsProvider>
          );
        }}
      />
      <Route
        exact
        path="/tabs/:tab(highscores)"
        render={(props) => {
          return user == null ? <Login /> : <Highscores {...props} />;
        }}
      />
      <Route
        exact
        path="/tabs/:tab(settings)"
        render={(props) => {
          return user == null ? <Login /> : <Settings {...props} />;
        }}
      />
    </>
  );

  return (
    <>
      <Notifications />
      <IonReactRouter>
        <DeepLinks />
        {user == null || hideBottomBar ? (
          <IonRouterOutlet id="main-content">{routes}</IonRouterOutlet>
        ) : (
          <IonTabs>
            <IonRouterOutlet id="main-content">{routes}</IonRouterOutlet>

            <IonTabBar slot="bottom" translucent="true">
              <IonTabButton tab="tournaments" href="/tabs/tournaments">
                <Md2Home className="h-6 w-6" />
                <IonLabel>{t("bottom_bar.leagues")}</IonLabel>
              </IonTabButton>
              <IonTabButton tab="explore" href="/tabs/explore">
                <Md2SearchWeb className="h-6 w-6" />
                <IonLabel>{t("bottom_bar.explore")}</IonLabel>
              </IonTabButton>
              <IonTabButton tab="footballData" href="/tabs/footballData">
                <Md2SoccerField className="h-6 w-6" />
                <IonLabel>{t("bottom_bar.events")}</IonLabel>
              </IonTabButton>
              <IonTabButton tab="highscores" href="/tabs/highscores">
                <Md2TrophyVariantOutline className="h-6 w-6" />
                <IonLabel className="whitespace-nowrap">
                  {t("bottom_bar.charts")}
                </IonLabel>
              </IonTabButton>
              <IonTabButton tab="settings" href="/tabs/settings">
                <Md2EmoticonExcitedOutline className="h-6 w-6" />
                <IonLabel>{t("bottom_bar.profile")}</IonLabel>
              </IonTabButton>
            </IonTabBar>
          </IonTabs>
        )}
      </IonReactRouter>
    </>
  );
};

export default AppRoutes;
